// Money parsing + formatting — extracted from components/bookings/format.ts.

// Parse a money field (string | number | null) to a number — the one home for
// the `parseFloat(String(v ?? 0)) || 0` idiom every deposit/total calc used to
// repeat. Blank / missing / junk → 0; a negative is preserved (a deposit can
// carry arrears forward from an unpaid previous stay).
export function toMoney(v: string | number | null | undefined): number {
  return parseFloat(String(v ?? 0)) || 0;
}

// A money field the USER is typing into, parsed strictly — `null` when the text
// is not (yet) a complete figure.
//
// WHY THIS EXISTS. The modal's money fields cannot be `<input type="number">`:
// that element's value-sanitisation algorithm replaces the intermediate "-" with
// "", so in a controlled React input the minus key is swallowed before it reaches
// state and a negative amount is literally untypeable. (Confirmed in Chrome:
// setting a number input's value to "-" reads back as "".) They are text inputs,
// which means their contents must be parsed rather than trusted.
//
// `toMoney` is the wrong tool here because it maps junk to 0, and 0 is a
// meaningful figure: a lone "-" from someone mid-keystroke would be read as
// "the deposit is now zero" and the save would book the whole recorded amount
// away as a negative adjustment. Blank IS zero — that is how a figure recorded
// in error gets cleared — but incomplete is not.
const MONEY_TEXT = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;
export function parseMoneyText(text: string): number | null {
  const t = text.trim();
  if (t === "") return 0;
  if (!MONEY_TEXT.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

// Render a whole-euro amount with the sign OUTSIDE the symbol — a credit reads
// "-€50", never "€-50". The one home for the table/desk/drawer due displays so
// the negative case formats identically everywhere.
export function fmtEuro(n: number): string {
  const r = Math.round(n);
  // The sign goes BEFORE the symbol, with a true minus (U+2212) — "−€50", never
  // "€-50" or a hyphen. Negative figures became ordinary once payment
  // adjustments (refunds, credits, arrears carried forward) were recordable.
  return (r < 0 ? "−€" : "€") + Math.abs(r).toLocaleString("en-IE");
}

// An EXACT amount, cents and all. A single payment is a record of what actually
// moved — €120.50 shown as €121 makes the authoritative ledger visibly disagree
// with what was entered. Whole euros stay whole (€50, not €50.00), so a ledger
// of round amounts still reads cleanly.
export function fmtEuroExact(n: number): string {
  const abs = Math.abs(n);
  const body = Number.isInteger(abs)
    ? abs.toLocaleString("en-IE")
    : abs.toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (n < 0 ? "−€" : "€") + body;
}
